import { useMemo } from "react";
import useApprovals from "../approvals/useApprovals";
import { useUserAccountRole } from "../permissions/useUserAccountRole";
import { ApprovalType } from "@zapier/utils";

interface Input {
  zapId?: string;
  isLoadingForApprovalModal?: boolean;
}

export function useZapApprovals({ zapId, isLoadingForApprovalModal }: Input) {
  const { customuserId: userId } = useUserAccountRole();

  const { approvals, isLoading } = useApprovals({
    zapId,
    isLoadingForApprovalModal,
    enabled: !!zapId, // Only fetch approvals for the Zap if zapId is provided
  });

  // There can only be one pending delete approval per zap
  const pendingDeleteApproval = useMemo(
    () =>
      approvals?.filter(
        (approval: { type: string }) => approval.type === ApprovalType.Delete,
      )[0],
    [approvals],
  );

  // There can only be one pending on/off approval per zap
  const pendingEnableOrDisableApproval = useMemo(
    () =>
      approvals?.filter(
        (approval: { type: string }) =>
          approval.type === ApprovalType.TurnOn ||
          approval.type === ApprovalType.TurnOff,
      )[0],
    [approvals],
  );

  const pendingPublishApproval = useMemo(
    () => approvals?.find((approval) => approval.type === ApprovalType.Publish),
    [approvals],
  );

  const userPendingApproval = useMemo(
    () =>
      approvals?.some(
        (pendingApproval: { requestedByUserId: number }) =>
          String(pendingApproval?.requestedByUserId) === String(userId),
      ),
    [approvals, userId],
  );

  return {
    isApprovalLoading: isLoading,
    pendingDeleteApproval,
    pendingEnableOrDisableApproval,
    pendingPublishApproval,
    userPendingApproval,
  };
}
